<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div>
    <!-- Toolbar -->
    <div class="plan-toolbar">
      <PrimeButton
        :label="t('adminClassificationLauncher.createPlan.button')"
        icon="ri-add-line"
        @click="showCreateDialog = true"
      />
      <PrimeButton
        :label="t('adminClassificationLauncher.importPlan.button')"
        icon="ri-upload-line"
        severity="secondary"
        outlined
        @click="triggerImport"
      />
      <input
        ref="fileInput"
        type="file"
        accept=".json"
        style="display: none"
        @change="handleFileImport"
      />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center p-3">
      <i class="ri-loader-4-line ri-spin" aria-hidden="true"></i>
      <span>{{ t("adminClassificationLauncher.existingPlans.loading") }}</span>
    </div>

    <!-- Plan table with selection bar + pagination -->
    <template v-else-if="plans.length > 0">
      <!-- Selection bar (marchés style) -->
      <div v-if="selectedNodes.length > 0" class="selection-bar">
        <div class="selection-info">
          <span class="selection-text">
            {{
              t("adminClassificationLauncher.selectionBar.selected", {
                count: selectedNodes.length,
                total: plans.length,
              })
            }}
          </span>
          <span
            v-if="!isAllSelected"
            class="select-all-link"
            @click="selectAll"
          >
            {{
              t("adminClassificationLauncher.selectionBar.selectAll", {
                total: plans.length,
              })
            }}
          </span>
          <span v-else class="select-all-link" @click="selectedNodes = []">
            {{ t("adminClassificationLauncher.selectionBar.clearSelection") }}
          </span>
        </div>

        <div class="selection-actions">
          <!-- Open in new tab -->
          <PrimeButton
            v-tooltip.top="
              t('adminClassificationLauncher.selectionBar.openAll')
            "
            icon="ri-external-link-line"
            rounded
            text
            severity="secondary"
            @click="openSelectedInNewTabs"
          />

          <div class="action-separator" />

          <!-- Download -->
          <PrimeButton
            v-tooltip.top="
              t('adminClassificationLauncher.selectionBar.downloadAll')
            "
            icon="ri-download-line"
            rounded
            text
            severity="secondary"
            @click="downloadSelected"
          />

          <!-- Delete -->
          <PrimeButton
            v-if="canDeleteSelected"
            v-tooltip.top="t('adminClassificationLauncher.deletePlan.button')"
            icon="ri-delete-bin-line"
            rounded
            text
            severity="secondary"
            @click="deleteSelected"
          />
        </div>
      </div>

      <ContentCompactTableView
        :files="paginatedPlans"
        :current-node="{ id: 'classification-plans' }"
        :datatable-column-config="columnConfig"
        :inline-actions="inlineActions"
        :selected-nodes="selectedNodes"
        @update:selected-nodes="selectedNodes = $event"
        @navigate-to="(node) => emit('select-plan', node.name)"
      />

      <!-- Paginator -->
      <PrimePaginator
        v-if="plans.length > rowsPerPage"
        :first="first"
        :rows="rowsPerPage"
        :total-records="plans.length"
        :rows-per-page-options="[10, 25, 50]"
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        @page="onPageChange"
      />
    </template>

    <!-- Empty state -->
    <div v-else class="p-3">
      <p>{{ t("adminClassificationLauncher.existingPlans.noPlansFound") }}</p>
    </div>

    <!-- Create dialog -->
    <PrimeDialog
      v-model:visible="showCreateDialog"
      :header="t('adminClassificationLauncher.createPlan.title')"
      modal
      :style="{ width: '30rem' }"
    >
      <ClassificationPlanCreate
        @create-plan="onCreatePlan"
        @cancel="showCreateDialog = false"
      />
    </PrimeDialog>

    <!-- Confirm dialog for delete -->
    <PrimeConfirmDialog group="deletePlan" />
  </div>
</template>

<script setup>
import { ref, computed, provide } from "vue";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { useI18n } from "vue-i18n";
import PrimeButton from "primevue/button";
import PrimeDialog from "primevue/dialog";
import PrimeConfirmDialog from "primevue/confirmdialog";
import PrimePaginator from "primevue/paginator";
import Tooltip from "primevue/tooltip";
import ContentCompactTableView from "../navigation/ContentCompactTableView.vue";
import ClassificationPlanCreate from "./ClassificationPlanCreate.vue";
import classificationService from "@/services/ClassificationService.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";

const vTooltip = Tooltip;

const props = defineProps({
  plans: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(["select-plan", "import-plan"]);

const toast = useToast();
const confirm = useConfirm();
const { t } = useI18n();

const fileInput = ref(null);
const showCreateDialog = ref(false);
const selectedNodes = ref([]);

// Pagination
const first = ref(0);
const rowsPerPage = ref(10);

const paginatedPlans = computed(() => {
  return props.plans.slice(first.value, first.value + rowsPerPage.value);
});

const onPageChange = (event) => {
  first.value = event.first;
  rowsPerPage.value = event.rows;
};

// Required by ContentCompactTableView (injected dependency)
provide("isCurrentNodeDisplayed", () => false);

const columnConfig = [
  {
    key: "name",
    label: t("adminClassificationLauncher.existingPlans.title"),
    visible: true,
  },
];

// --- Single-row inline actions ---

const downloadPlan = (event, node) => {
  classificationService
    .getPlan(node.name)
    .then((jsonContent) => {
      const blob = new Blob([JSON.stringify(jsonContent, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = node.name;
      link.click();
      URL.revokeObjectURL(url);
    })
    .catch((err) => {
      console.error("Failed to download plan", err);
      toast.add({
        severity: "error",
        summary: t("adminClassificationLauncher.toast.listError.summary"),
        detail: t("adminClassificationLauncher.toast.listError.detail"),
        life: 4000,
      });
    });
};

const deletePlan = (event, node) => {
  confirm.require({
    message: t("adminClassificationLauncher.deletePlan.confirmMessage", {
      name: node.name,
    }),
    header: t("adminClassificationLauncher.deletePlan.confirmHeader"),
    icon: "ri-error-warning-line",
    group: "deletePlan",
    acceptProps: { severity: "danger" },
    accept: () => {
      alfrescoNodeService
        .deleteNode(node.id, "fichier")
        .then(() => {
          toast.add({
            severity: "success",
            summary: t(
              "adminClassificationLauncher.deletePlan.toast.success.summary",
            ),
            detail: t(
              "adminClassificationLauncher.deletePlan.toast.success.detail",
              { name: node.name },
            ),
            life: 3000,
          });
          selectedNodes.value = selectedNodes.value.filter(
            (n) => n.id !== node.id,
          );
          emit("import-plan");
        })
        .catch((err) => {
          console.error("Failed to delete plan", err);
          toast.add({
            severity: "error",
            summary: t(
              "adminClassificationLauncher.deletePlan.toast.error.summary",
            ),
            detail: t(
              "adminClassificationLauncher.deletePlan.toast.error.detail",
            ),
            life: 4000,
          });
        });
    },
  });
};

const inlineActions = [
  {
    icon: "ri-download-line",
    tooltip: t("adminClassificationLauncher.existingPlans.download"),
    handler: downloadPlan,
  },
  {
    icon: "ri-delete-bin-line",
    tooltip: t("adminClassificationLauncher.deletePlan.button"),
    severity: "danger",
    handler: deletePlan,
    visible: (node) => node.allowableOperations?.includes("delete"),
  },
];

// --- Selection bar bulk actions ---

const isAllSelected = computed(() => {
  return (
    selectedNodes.value.length === props.plans.length && props.plans.length > 0
  );
});

const selectAll = () => {
  selectedNodes.value = [...props.plans];
};

const canDeleteSelected = computed(() => {
  return (
    selectedNodes.value.length > 0 &&
    selectedNodes.value.every((node) =>
      node.allowableOperations?.includes("delete"),
    )
  );
});

const downloadSelected = () => {
  selectedNodes.value.forEach((node) => {
    downloadPlan(null, node);
  });
};

const openSelectedInNewTabs = () => {
  selectedNodes.value.forEach((node) => {
    emit("select-plan", node.name);
  });
};

const deleteSelected = () => {
  const names = selectedNodes.value.map((n) => n.name).join(", ");
  confirm.require({
    message: t(
      "adminClassificationLauncher.deletePlan.confirmMessageMultiple",
      {
        count: selectedNodes.value.length,
        names,
      },
    ),
    header: t("adminClassificationLauncher.deletePlan.confirmHeader"),
    icon: "ri-error-warning-line",
    group: "deletePlan",
    acceptProps: { severity: "danger" },
    accept: () => {
      const deletePromises = selectedNodes.value.map((node) =>
        alfrescoNodeService.deleteNode(node.id, "fichier"),
      );
      Promise.allSettled(deletePromises).then((results) => {
        const successes = results.filter(
          (r) => r.status === "fulfilled",
        ).length;
        const failures = results.filter((r) => r.status === "rejected").length;
        if (successes > 0) {
          toast.add({
            severity: "success",
            summary: t(
              "adminClassificationLauncher.deletePlan.toast.success.summary",
            ),
            detail: t(
              "adminClassificationLauncher.deletePlan.toast.successMultiple.detail",
              { count: successes },
            ),
            life: 3000,
          });
        }
        if (failures > 0) {
          toast.add({
            severity: "error",
            summary: t(
              "adminClassificationLauncher.deletePlan.toast.error.summary",
            ),
            detail: t(
              "adminClassificationLauncher.deletePlan.toast.errorMultiple.detail",
              { count: failures },
            ),
            life: 4000,
          });
        }
        selectedNodes.value = [];
        emit("import-plan");
      });
    },
  });
};

// --- Import ---

const triggerImport = () => {
  fileInput.value.click();
};

const handleFileImport = (event) => {
  const file = event.target.files[0];
  if (!file) {
    return;
  }

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const importedData = JSON.parse(e.target.result);

      if (
        !importedData.NATURES ||
        !importedData.FIELDS ||
        !importedData.EMPLACEMENT
      ) {
        throw new Error(
          t("adminClassificationLauncher.importPlan.invalidStructure"),
        );
      }

      let filename = file.name.endsWith(".json")
        ? file.name
        : `${file.name}.json`;
      if (!filename.startsWith("plan-")) {
        filename = `plan-${filename}`;
      }

      classificationService
        .savePlan(filename, importedData)
        .then(() => {
          toast.add({
            severity: "success",
            summary: t(
              "adminClassificationLauncher.importPlan.toast.success.summary",
            ),
            detail: t(
              "adminClassificationLauncher.importPlan.toast.success.detail",
              { filename },
            ),
            life: 3000,
          });
          emit("import-plan");
          emit("select-plan", filename);
        })
        .catch((err) => {
          console.error("Failed to save imported plan", err);
          toast.add({
            severity: "error",
            summary: t(
              "adminClassificationLauncher.importPlan.toast.saveError.summary",
            ),
            detail: t(
              "adminClassificationLauncher.importPlan.toast.saveError.detail",
            ),
            life: 4000,
          });
        });
    } catch (error) {
      toast.add({
        severity: "error",
        summary: t(
          "adminClassificationLauncher.importPlan.toast.parseError.summary",
        ),
        detail: t(
          "adminClassificationLauncher.importPlan.toast.parseError.detail",
          { message: error.message },
        ),
        life: 5000,
      });
    }
  };
  reader.readAsText(file);
  event.target.value = "";
};

const onCreatePlan = (filename) => {
  showCreateDialog.value = false;
  emit("select-plan", filename);
};
</script>

<style lang="scss" scoped>
.plan-toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.selection-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;
  background: color-mix(
    in srgb,
    var(--p-primary-color) 15%,
    var(--p-content-background)
  );
  border-left: 4px solid var(--p-primary-color);
  border-radius: 0.5rem;
  gap: 1rem;
}

.selection-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.selection-text {
  font-weight: 500;
  color: var(--p-text-color);
}

.select-all-link {
  font-weight: 600;
  color: var(--p-primary-color);
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--p-primary-hover-color);
  }
}

.selection-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.action-separator {
  width: 1px;
  height: 1.5rem;
  background-color: var(--p-surface-border);
  margin: 0 0.5rem;
}
</style>
